import {
  AuthError,
  ClientError,
  ForbiddenError,
  getUserFriendlyErrorMessage,
  isNetworkError,
  isServerError,
} from "#lib/errors"
import { queryKeys } from "#lib/query-keys"
import { homeFinancialSDK } from "#sdk/sdk"
import type { Account, AccountResponse } from "@home-finance-manager/shared/types/accounts"
import { User } from "@home-finance-manager/shared/types/users"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

function convertAccountResponsesToAccounts(accountResponses: AccountResponse[]): Account[] {
  return accountResponses.map((acc) => ({
    id: Number(acc.id),
    name: acc.name,
    createdAt: new Date(acc.createdAt),
    updatedAt: new Date(acc.updatedAt),
    meta: acc.meta,
  }))
}

const getStoredUser = (): User | null => {
  try {
    const userData = localStorage.getItem("user_data")
    if (!userData) return null
    return JSON.parse(userData)
  } catch (error) {
    console.error("Failed to parse stored user data:", error)
    localStorage.removeItem("user_data")
    return null
  }
}

export const storeUser = (user: User): void => {
  try {
    const user_ = { ...user }
    user_.fullName ??= user.email.split("@")[0]
    user_.avatar ??= `https://ui-avatars.com/api/?name=${user.email.split("@")[0]}&background=random`
    user_.emailConfirmedAt ??= null
    user_.tourStartedAt ??= null
    user_.tourCompletedAt ??= null
    user_.tourStepIndex ??= null
    localStorage.setItem("user_data", JSON.stringify(user_))
  } catch (error) {
    console.error("Failed to store user data:", error)
    throw new Error("Failed to save user data")
  }
}

const getStoredToken = (): string | null => {
  return localStorage.getItem("auth_token")
}

export const storeToken = (token: string): void => {
  try {
    if (!token || typeof token !== "string") {
      throw new Error("Invalid token")
    }
    localStorage.setItem("auth_token", token)
  } catch (error) {
    console.error("Failed to store auth token:", error)
    throw new Error("Failed to save authentication token")
  }
}

const clearAuth = (): void => {
  try {
    localStorage.removeItem("auth_token")
    localStorage.removeItem("user_data")
    localStorage.removeItem("current_account")
  } catch (error) {
    console.error("Failed to clear auth data:", error)
  }
}

export function useUser() {
  return useQuery({
    queryKey: queryKeys.auth.user(),
    queryFn: () => {
      const user = getStoredUser()
      const token = getStoredToken()
      if (!user || !token) {
        return null
      }
      return user
    },
    staleTime: 0,
    initialData: getStoredUser,
    refetchOnMount: () => {
      const token = getStoredToken()
      return !!token
    },
    refetchOnWindowFocus: false,
    retry: false,
  })
}

export function useVerifyEmail() {
  const { data: user } = useUser()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (token: string) => {
      const response = await homeFinancialSDK.users.verifyEmail(token)
      return response.message
    },
    onSuccess: () => {
      const user_ = { ...user, emailConfirmedAt: new Date() }
      storeUser(user_ as unknown as User)
      queryClient.setQueryData(queryKeys.auth.user(), user_)
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.user() })
      queryClient.invalidateQueries({ queryKey: queryKeys.accounts.all })
    },
    onError: (error) => {
      console.error("Verify email error:", error)
    },
  })
}

export function useIsAuthenticated() {
  const token = getStoredToken()
  const storedUser = getStoredUser()
  return !!storedUser && !!token
}

export function useLogin() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ email, password }: { email: string; password: string }) => {
      try {
        const response = await homeFinancialSDK.users.login(email, password)

        if (!response.token?.token) {
          throw new ClientError("Invalid response from server", "INVALID_RESPONSE", 500)
        }

        if (!response.user) {
          throw new ClientError("User data not received", "INVALID_RESPONSE", 500)
        }

        storeToken(response.token.token)
        storeUser(response.user)

        return response.user
      } catch (error) {
        throw error
      }
    },
    onSuccess: (user) => {
      queryClient.setQueryData(queryKeys.auth.user(), user)

      if (user.accounts && Array.isArray(user.accounts) && user.accounts.length > 0) {
        const accounts = convertAccountResponsesToAccounts(user.accounts)
        queryClient.setQueryData(queryKeys.accounts.lists(), accounts)

        const currentAccount = queryClient.getQueryData<Account | null>(
          queryKeys.accounts.current()
        )
        if (!currentAccount) {
          const defaultAccount = accounts.find((acc) => acc.meta?.pivot_is_default) || accounts[0]
          if (defaultAccount) {
            localStorage.setItem("current_account", JSON.stringify(defaultAccount))
            queryClient.setQueryData(queryKeys.accounts.current(), defaultAccount)
          }
        }
      } else {
        queryClient.invalidateQueries({ queryKey: queryKeys.accounts.all })
      }
    },
    onError: (error) => {
      console.error("Login error:", error)
    },
    retry: (failureCount, error) => {
      if (error instanceof AuthError) {
        return false
      }
      if (error instanceof ForbiddenError) {
        return false
      }
      if (error instanceof ClientError) {
        return false
      }
      if (isServerError(error)) {
        return failureCount < 2
      }
      if (isNetworkError(error)) {
        return failureCount < 2
      }
      return false
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  })
}

export function useRegister() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      email,
      password,
      invitationToken,
    }: {
      email: string
      password: string
      invitationToken?: string
    }) => {
      try {
        const response = await homeFinancialSDK.users.register(email, password, invitationToken)

        if (!response.token?.token) {
          throw new ClientError("Invalid response from server", "INVALID_RESPONSE", 500)
        }

        if (!response.user) {
          throw new ClientError("User data not received", "INVALID_RESPONSE", 500)
        }

        storeToken(response.token.token)
        storeUser(response.user)

        return response.user
      } catch (error) {
        throw error
      }
    },
    onSuccess: (user) => {
      queryClient.setQueryData(queryKeys.auth.user(), user)
      if (user.accounts && Array.isArray(user.accounts) && user.accounts.length > 0) {
        const accounts = convertAccountResponsesToAccounts(user.accounts)
        queryClient.setQueryData(queryKeys.accounts.lists(), accounts)

        const currentAccount = queryClient.getQueryData<Account | null>(
          queryKeys.accounts.current()
        )
        if (!currentAccount) {
          const defaultAccount = accounts.find((acc) => acc.meta?.pivot_is_default) || accounts[0]
          if (defaultAccount) {
            localStorage.setItem("current_account", JSON.stringify(defaultAccount))
            queryClient.setQueryData(queryKeys.accounts.current(), defaultAccount)
          }
        }
      } else {
        queryClient.invalidateQueries({ queryKey: queryKeys.accounts.all })
      }
    },
    onError: (error) => {
      console.error("Registration error:", error)
    },
    retry: (failureCount, error) => {
      if (error instanceof AuthError) {
        return false
      }
      if (error instanceof ForbiddenError) {
        return false
      }
      if (error instanceof ClientError) {
        return false
      }
      if (isServerError(error)) {
        return failureCount < 2
      }
      if (isNetworkError(error)) {
        return failureCount < 2
      }
      return false
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  })
}

export function useLogout() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      try {
        await queryClient.cancelQueries()

        queryClient.resetQueries({ queryKey: queryKeys.auth.user() })
        queryClient.setQueryData(queryKeys.auth.user(), null)

        queryClient.removeQueries()

        // Now safe to clear auth token
        clearAuth()
      } catch (error) {
        console.error("Error during logout:", error)
        queryClient.resetQueries({ queryKey: queryKeys.auth.user() })
        queryClient.setQueryData(queryKeys.auth.user(), null)
      }
    },
    onSuccess: () => {
      queryClient.clear()
      queryClient.setQueryData(queryKeys.auth.user(), null)
    },
    onError: (error) => {
      console.error("Logout error:", error)
      queryClient.clear()
      queryClient.setQueryData(queryKeys.auth.user(), null)
    },
    retry: false,
  })
}

export function useAuthError(mutation: { error: unknown } | null): string | null {
  if (!mutation?.error) return null
  return getUserFriendlyErrorMessage(mutation.error)
}
